/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { CSSProperties, ReactNode } from 'react';

/**
 * Modal

 * props.className - className applied to modal
 * props.style - style object applied to modal
 * props.containerStyle - style object applied to modal
 * [props.headerContainerStyles = {}] - style object applied to modal header container
 * props.header - Header text
 * props.subHeader - Subheader text
 * props.footer - footer component
 * [props.showCloseLeft] - footer component
 * [props.showCloseLeftStyles] - style object applied to component in left top corner
 */

export interface ModalProps {
    controlId?: string;
    containerStyle?: CSSProperties;
    style?: CSSProperties;
    showClose?: boolean;
    children: ReactNode;
    headerDivider?: boolean;
    inline?: boolean;
    footer?: ReactNode;
    className?: string;
    onCloseClick?: Function;
    headerStyles?: CSSProperties;
    subCloseIconMessage?: ReactNode;
    header?: ReactNode;
    subHeader?: ReactNode;
    subHeaderStyles?: CSSProperties;
    showCloseLeft?: ReactNode;
    requiredFields?: boolean;
    showCloseLeftStyles?: CSSProperties;
    subCloseIconMessageStyles?: CSSProperties;
    headerContainerStyles?: CSSProperties;
}

const Modal = ({
    className = '',
    subHeader = '',
    controlId = 'MotiliModal',
    style = {},
    containerStyle = {},
    showClose = false,
    inline = false,
    headerStyles = { fontSize: 21, fontWeight: 600 },
    subHeaderStyles = { fontSize: 16, fontWeight: 'normal' },
    headerContainerStyles,
    subCloseIconMessage,
    header,
    showCloseLeft,
    requiredFields,
    headerDivider,
    showCloseLeftStyles,
    subCloseIconMessageStyles,
    onCloseClick,
    children,
    footer,
}: ModalProps) => {
    return (
        <div id={controlId} style={{ ...styles.modal, ...containerStyle }}>
            <div
                style={{ ...styles.modalContent, ...style }}
                className={className}
                data-test-id='modal-content'
            >
                {!inline && (
                    <>
                        <Header
                            headerStyles={headerStyles}
                            headerContainerStyles={headerContainerStyles}
                            showClose={showClose}
                            subCloseIconMessage={subCloseIconMessage}
                            header={header}
                            subHeader={subHeader}
                            subHeaderStyles={subHeaderStyles}
                            showCloseLeft={showCloseLeft}
                            requiredFields={requiredFields}
                            headerDivider={headerDivider}
                            showCloseLeftStyles={showCloseLeftStyles}
                            subCloseIconMessageStyles={
                                subCloseIconMessageStyles
                            }
                            onCloseClick={onCloseClick}
                        />
                        {headerDivider && <DividerLine />}
                        {children}
                        {footer && <Footer footer={footer} />}
                    </>
                )}
                {inline && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {showClose && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <i
                                    onClick={e => {
                                        if (onCloseClick) {
                                            onCloseClick(e);
                                        }
                                    }}
                                    className='fa fa-angle-times'
                                    style={{
                                        color: '#3b3b3b',
                                        cursor: 'pointer',
                                        marginRight: 10,
                                    }}
                                />
                            </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {children}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Modal;

type HeaderProps = Pick<
    ModalProps,
    | 'headerStyles'
    | 'headerContainerStyles'
    | 'showClose'
    | 'subCloseIconMessage'
    | 'header'
    | 'subHeader'
    | 'subHeaderStyles'
    | 'showCloseLeft'
    | 'requiredFields'
    | 'headerDivider'
    | 'showCloseLeftStyles'
    | 'subCloseIconMessageStyles'
    | 'onCloseClick'
>;

const Header = ({
    headerDivider,
    headerContainerStyles,
    header,
    headerStyles,
    subHeaderStyles,
    subHeader,
    showCloseLeft,
    showCloseLeftStyles,
    showClose,
    onCloseClick,
    subCloseIconMessage,
    subCloseIconMessageStyles,
}: HeaderProps) => {
    return (
        <div>
            <div
                style={
                    headerDivider
                        ? { ...styles.headerContent, marginBottom: 0 }
                        : {
                              ...styles.headerContent,
                              ...headerContainerStyles,
                          }
                }
            >
                {header && (
                    <div style={{ flex: 1, ...headerStyles }}>
                        {header}
                        <div style={{ ...subHeaderStyles }}>{subHeader}</div>
                    </div>
                )}
                {showCloseLeft && (
                    <div
                        style={{
                            ...styles.closeLeftButton,
                            ...showCloseLeftStyles,
                        }}
                    >
                        {showCloseLeft}
                    </div>
                )}
                {showClose && (
                    <i
                        onClick={e => {
                            if (onCloseClick) {
                                onCloseClick(e);
                            }
                        }}
                        className='fa fa-angle-times'
                        style={styles.closeButton}
                    />
                )}
                {subCloseIconMessage && (
                    <div
                        className='subCloseIconMessage'
                        style={{
                            ...styles.subCloseIconMessage,
                            ...subCloseIconMessageStyles,
                        }}
                    >
                        {subCloseIconMessage}
                    </div>
                )}
            </div>
        </div>
    );
};
function Footer({ footer }: Pick<ModalProps, 'footer'>) {
    return <div style={{ margin: '30px 0' }}>{footer}</div>;
}

const DividerLine = () => {
    return (
        <div
            style={{
                borderBottom: '0.5px solid #818181',
                width: '100%',
            }}
        />
    );
};

const styles: { [key: string]: CSSProperties } = {
    modal: {
        display: 'block',
        position: 'fixed' /* Stay in place */,
        zIndex: 50 /* Sit on top */,
        paddingTop: 100 /* Location of the box */,
        paddingBottom: 100,
        left: 0,
        top: 0,
        width: '100%' /* Full width */,
        height: '100%' /* Full height */,
        overflow: 'auto' /* Enable scroll if needed */,
        backgroundColor: 'rgba(0,0,0,0.4)' /* Black w/ opacity */,
    },
    modalContent: {
        position: 'relative',
        backgroundColor: 'white',
        margin: 'auto',
        padding: '50px 50px',
        border: '1px solid #888',
        width: '80%',
        fontSize: 14,
        maxWidth: 850,
        boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2),0 6px 20px 0 rgba(0,0,0,0.19)',
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 21,
        color: '#3b3b3b',
        paddingBottom: 20,
        marginBottom: 20,
    },
    closeButton: {
        position: 'absolute',
        right: 25,
        top: 15,
        fontSize: 25,
        cursor: 'pointer',
        color: 'gray',
    },
    closeLeftButton: {
        position: 'absolute',
        left: 25,
        top: 15,
    },
    subCloseIconMessage: {
        right: '17px',
        top: '60px',
        position: 'absolute',
        color: 'gray',
    },
    requiredFieldsText: {
        color: '#3f82a5',
        fontSize: 14,
        fontWeight: 'bold',
        fontStyle: 'italic',
    },
};
